export type OverlayCoordinateSpace = 'model_input' | 'source_pixel' | 'normalized_source' | 'canvas_pixel';

export interface OverlayPoint {
    x: number;
    y: number;
}

export interface RenderedVideoRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface OverlaySourceTransform {
    coordinateSpace: OverlayCoordinateSpace;
    sourceWidth: number;
    sourceHeight: number;
    coordinateWidth?: number;
    coordinateHeight?: number;
    modelWidth?: number;
    modelHeight?: number;
    letterboxScale?: number;
    letterboxPadX?: number;
    letterboxPadY?: number;
}

export const OVERLAY_TRANSFORM_VERSION = 'source-pixel-v2';

const finite = (value: unknown, fallback: number = 0): number => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
};

export const clamp = (value: number, minimum: number, maximum: number): number => (
    Math.max(minimum, Math.min(maximum, finite(value, minimum)))
);

export function calculateContainedVideoRect(
    containerWidth: number,
    containerHeight: number,
    videoWidth: number,
    videoHeight: number,
): RenderedVideoRect {
    const safeContainerWidth = Math.max(1, finite(containerWidth, 1));
    const safeContainerHeight = Math.max(1, finite(containerHeight, 1));
    const safeVideoWidth = Math.max(1, finite(videoWidth, 1));
    const safeVideoHeight = Math.max(1, finite(videoHeight, 1));
    const displayScale = Math.min(
        safeContainerWidth / safeVideoWidth,
        safeContainerHeight / safeVideoHeight,
    );
    const width = safeVideoWidth * displayScale;
    const height = safeVideoHeight * displayScale;
    return {
        x: (safeContainerWidth - width) / 2,
        y: (safeContainerHeight - height) / 2,
        width,
        height,
    };
}

export function modelInputPointToSourcePoint(
    point: OverlayPoint,
    transform: OverlaySourceTransform,
    clampToSource: boolean = true,
): OverlayPoint {
    const sourceWidth = Math.max(1, finite(transform.sourceWidth, 1));
    const sourceHeight = Math.max(1, finite(transform.sourceHeight, 1));
    const modelWidth = Math.max(1, finite(transform.modelWidth ?? transform.coordinateWidth, sourceWidth));
    const modelHeight = Math.max(1, finite(transform.modelHeight ?? transform.coordinateHeight, sourceHeight));
    const scale = Math.max(1e-9, finite(
        transform.letterboxScale,
        Math.min(modelWidth / sourceWidth, modelHeight / sourceHeight),
    ));
    const resizedWidth = sourceWidth * scale;
    const resizedHeight = sourceHeight * scale;
    const padX = finite(transform.letterboxPadX, (modelWidth - resizedWidth) / 2);
    const padY = finite(transform.letterboxPadY, (modelHeight - resizedHeight) / 2);
    const x = (finite(point.x) - padX) / scale;
    const y = (finite(point.y) - padY) / scale;
    return clampToSource ? { x: clamp(x, 0, sourceWidth), y: clamp(y, 0, sourceHeight) } : { x, y };
}

export function coordinatePointToSourcePoint(
    point: OverlayPoint,
    transform: OverlaySourceTransform,
    clampToSource: boolean = true,
): OverlayPoint {
    const sourceWidth = Math.max(1, finite(transform.sourceWidth, 1));
    const sourceHeight = Math.max(1, finite(transform.sourceHeight, 1));
    if (transform.coordinateSpace === 'model_input') {
        return modelInputPointToSourcePoint(point, transform, clampToSource);
    }
    if (transform.coordinateSpace === 'normalized_source') {
        const x = finite(point.x) * sourceWidth;
        const y = finite(point.y) * sourceHeight;
        return clampToSource ? { x: clamp(x, 0, sourceWidth), y: clamp(y, 0, sourceHeight) } : { x, y };
    }
    const coordinateWidth = Math.max(1, finite(transform.coordinateWidth, sourceWidth));
    const coordinateHeight = Math.max(1, finite(transform.coordinateHeight, sourceHeight));
    const x = finite(point.x) * sourceWidth / coordinateWidth;
    const y = finite(point.y) * sourceHeight / coordinateHeight;
    return clampToSource ? { x: clamp(x, 0, sourceWidth), y: clamp(y, 0, sourceHeight) } : { x, y };
}

export function sourcePointToCanvasPoint(
    sourceX: number,
    sourceY: number,
    sourceWidth: number,
    sourceHeight: number,
    renderedVideoRect: RenderedVideoRect,
): OverlayPoint {
    const safeSourceWidth = Math.max(1, finite(sourceWidth, 1));
    const safeSourceHeight = Math.max(1, finite(sourceHeight, 1));
    return {
        x: renderedVideoRect.x + clamp(sourceX, 0, safeSourceWidth) / safeSourceWidth * renderedVideoRect.width,
        y: renderedVideoRect.y + clamp(sourceY, 0, safeSourceHeight) / safeSourceHeight * renderedVideoRect.height,
    };
}

export function canvasPointToSourcePoint(
    canvasX: number,
    canvasY: number,
    sourceWidth: number,
    sourceHeight: number,
    renderedVideoRect: RenderedVideoRect,
): OverlayPoint {
    const width = Math.max(1e-9, finite(renderedVideoRect.width, 1));
    const height = Math.max(1e-9, finite(renderedVideoRect.height, 1));
    const safeSourceWidth = Math.max(1, finite(sourceWidth, 1));
    const safeSourceHeight = Math.max(1, finite(sourceHeight, 1));
    return {
        x: clamp((finite(canvasX) - renderedVideoRect.x) / width * safeSourceWidth, 0, safeSourceWidth),
        y: clamp((finite(canvasY) - renderedVideoRect.y) / height * safeSourceHeight, 0, safeSourceHeight),
    };
}

export function clampSourcePolygon(
    points: OverlayPoint[],
    sourceWidth: number,
    sourceHeight: number,
): OverlayPoint[] {
    const safeSourceWidth = Math.max(1, finite(sourceWidth, 1));
    const safeSourceHeight = Math.max(1, finite(sourceHeight, 1));
    return (Array.isArray(points) ? points : [])
        .filter(point => Number.isFinite(Number(point?.x)) && Number.isFinite(Number(point?.y)))
        .map(point => ({
            x: clamp(Number(point.x), 0, safeSourceWidth),
            y: clamp(Number(point.y), 0, safeSourceHeight),
        }));
}

export function resizeCanvasForDisplay(
    canvas: HTMLCanvasElement,
    cssWidth: number,
    cssHeight: number,
    requestedDpr?: number,
): { context: CanvasRenderingContext2D | null; cssWidth: number; cssHeight: number; dpr: number } {
    const width = Math.max(1, Math.round(finite(cssWidth, 1)));
    const height = Math.max(1, Math.round(finite(cssHeight, 1)));
    const browserDpr = typeof window !== 'undefined' ? finite(window.devicePixelRatio, 1) : 1;
    const dpr = clamp(finite(requestedDpr, browserDpr), 1, 3);
    const backingWidth = Math.max(1, Math.round(width * dpr));
    const backingHeight = Math.max(1, Math.round(height * dpr));
    if (canvas.width !== backingWidth || canvas.height !== backingHeight) {
        canvas.width = backingWidth;
        canvas.height = backingHeight;
    }
    const context = canvas.getContext('2d');
    context?.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { context, cssWidth: width, cssHeight: height, dpr };
}
